import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
} from '@/components/ui-kit/dialog';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Switch } from '@/components/ui-kit/switch';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import { useAddLink } from '@/modules/gpt-chats/hooks/use-kb';

import { isErrorWithErrors } from '@/modules/gpt-chats/utils/error';
import { Button } from '@/components/ui-kit/button';
import { z } from 'zod';
import { Plus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

export const getLinkKbFormSchema = (t: TFunction) =>
  z.object({
    link: z
      .string()
      .trim()
      .url({ message: t('MUST_BE_VALID_URL') }),
    allow_recursive: z.boolean(),
  });

export type LinkKbFormValues = z.infer<ReturnType<typeof getLinkKbFormSchema>>;

export const linkKbFormDefaultValues: LinkKbFormValues = {
  link: '',
  allow_recursive: false,
};

type AddLinkModalProps = {
  folderId: string;
};

export const AddKbLinkModal = ({ folderId }: AddLinkModalProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const form = useForm<LinkKbFormValues>({
    defaultValues: linkKbFormDefaultValues,
    resolver: zodResolver(getLinkKbFormSchema(t)),
  });

  const { isDirty, isValid } = form.formState;

  const { mutateAsync, isPending } = useAddLink();

  const submitHandler = async (values: LinkKbFormValues) => {
    try {
      if (!folderId) return toast.error({ errors: t('SOMETHING_WENT_WRONG') });
      const res = await mutateAsync({
        link: values.link,
        folder_id: folderId,
        allow_recursive: values.allow_recursive || false,
      });
      if (!res.is_success) return toast.error({ errors: res.error });
      toast.success(t('NEW_LINK_ADDED'));
      form.reset();
      setOpen(false);
    } catch (error) {
      if (isErrorWithErrors(error)) return toast.error({ errors: error.errors });
      toast.error({ errors: t('SOMETHING_WENT_WRONG') });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) form.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          {t('ADD_LINK')}
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-6">
        <DialogHeader>
          <DialogTitle>{t('ADD_LINK')}</DialogTitle>
          <DialogDescription>{t('PROVIDE_URL_DESCRIPTION')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submitHandler)} className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('LINK_LABEL')}</FormLabel>
                  <FormControl>
                    <Input placeholder="https://example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="allow_recursive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>{t('RECURSIVE')}</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4">
              <DialogClose asChild>
                <Button variant="outline" size="sm" className="w-20">
                  {t('CANCEL')}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                size="sm"
                className="min-w-20"
                disabled={isPending || !isValid || !isDirty}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('ADDING')}
                  </>
                ) : (
                  t('ADD')
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
